import axios from "axios";
import TokenService from "./token.service";
import i18n from "../lang/i18n";
import {
  markSessionExpired,
  rememberReturnTo,
} from "../utils/helpers/sessionHandoff";

const baseURL = process.env.REACT_APP_API_URL;

const bilAPI = axios.create({
  baseURL,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

bilAPI.interceptors.request.use(
  (config) => {
    const token = TokenService.getLocalAccessToken();
    if (token) config.headers.Authorization = `Bearer ${token}`;
    // Drives Django's LocaleMiddleware, and through it django-modeltranslation:
    // it decides which of name_en / name_kk / name_ru the API serves back.
    //
    // `resolvedLanguage`, NOT `language`. The latter returns the tag that was
    // REQUESTED — which for an unsupported or stale value (e.g. a leftover
    // "kz") is a language we cannot render and the server does not accept.
    // `resolvedLanguage` is what i18next actually fell back to.
    config.headers["Accept-Language"] = i18n.resolvedLanguage || "en";
    return config;
  },
  (error) => Promise.reject(error),
);

// A single shared refresh across concurrent 401s. With rotating refresh tokens,
// letting each parallel request refresh on its own would invalidate the others
// and log the user out mid-session — so they all await one in-flight refresh.
let refreshPromise = null;

bilAPI.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalConfig = error?.config;

    // No response = network/timeout error; nothing to refresh.
    if (!error?.response || !originalConfig) return Promise.reject(error);

    const isAuthEndpoint =
      originalConfig.url?.includes("/auth/login/") ||
      originalConfig.url?.includes("/auth/refresh/");

    if (
      error.response.status === 401 &&
      !originalConfig._retry &&
      !isAuthEndpoint
    ) {
      originalConfig._retry = true; // guard against a refresh loop

      // Another TAB may have refreshed while this request was in flight. The
      // single-flight promise above is per document, but the tokens are shared
      // in localStorage — and the backend rotates and blacklists, so spending
      // our copy of the refresh token would log both tabs out. If the stored
      // access token has moved on from the one this request carried, that
      // refresh already happened: just retry with the new one.
      const sentToken = originalConfig.headers?.Authorization;
      const storedToken = TokenService.getLocalAccessToken();
      if (storedToken && sentToken && sentToken !== `Bearer ${storedToken}`) {
        return bilAPI(originalConfig);
      }

      try {
        if (!refreshPromise) {
          refreshPromise = axios
            .post(`${baseURL}/auth/refresh/`, {
              refresh: TokenService.getLocalRefreshToken(),
            })
            .then((rs) => {
              TokenService.updateLocalAccessToken(rs.data.access);
              // ROTATE_REFRESH_TOKENS is on, so the response carries a NEW
              // refresh token and blacklists the one we just spent. Dropping it
              // meant the SECOND refresh always sent a blacklisted token — a
              // forced sign-out for every user roughly every two days.
              if (rs.data.refresh) {
                TokenService.updateLocalRefreshToken(rs.data.refresh);
              }
              return rs.data.access;
            })
            .finally(() => {
              refreshPromise = null;
            });
        }
        await refreshPromise;
        return bilAPI(originalConfig);
      } catch (refreshError) {
        // Refresh failed: drop tokens and bounce to login instead of leaving
        // the user on a broken, half-authed page. Hand the login page enough
        // to explain itself and to put the person back where they were.
        TokenService.removeTokens();
        if (window.location.pathname !== "/login") {
          markSessionExpired();
          rememberReturnTo(
            `${window.location.pathname}${window.location.search}`,
          );
          window.location.assign("/login");
        }
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  },
);

export default bilAPI;
